import React, { useState } from "react";
import { CalendarIcon, UsersIcon } from "@heroicons/react/24/outline";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { motion } from "framer-motion";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { StarIcon, FunnelIcon } from "@heroicons/react/24/outline";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import { DateTime } from "luxon";
import { ProgressBar } from "@tripbuilder/common/ProgressBar";
import useCustomAccommodationStore from "@/stores/tripBuilder/useCustomAccommodation";
import { questions } from "./custom/questions";
import logger from "@/utils/logger";

interface SearchInputProps {
  effectiveCheckIn: string;
}

const SearchInput: React.FC<SearchInputProps> = ({ effectiveCheckIn }) => {
  const { selectedLocation } = useTripStore();
  const {
    activeAccommodationTab,
    selectedRating,
    selectedPriceRange,
    setSelectedRating,
    setSelectedPriceRange,
  } = useAccommodationStore();
  const currentStepIndex = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const totalSteps = questions.length;

  const activeFilters = [selectedRating, selectedPriceRange].filter(
    (filter) => filter !== null
  ).length;

  const [isOpen, setIsOpen] = useState(false);

  const scores = [7, 8, 9];
  const priceCategories = [
    { label: "Budget", value: "budget" },
    { label: "Mid-Range", value: "mid" },
    { label: "High-End", value: "high" },
    { label: "Luxury", value: "luxury" },
  ];

  const handleRatingChange = (score: number) => {
    setSelectedRating(selectedRating === score ? null : score);
  };

  const handlePriceChange = (value: string) => {
    setSelectedPriceRange(selectedPriceRange === value ? null : value);
  };

  logger.log("Effective Check In", effectiveCheckIn);

  if (activeAccommodationTab === "custom") {
    return (
      <ProgressBar currentStep={currentStepIndex} totalSteps={totalSteps} />
    );
  }

  return (
    <div className="space-y-2 w-full mt-4">
      <div className="flex items-stretch space-x-2 text-sm h-10">
        <div className="flex-1 grid grid-cols-2 gap-x-2">
          <div className="rounded-lg ring-1 ring-neutral-200 relative flex flex-col justify-center h-full">
            <div className="absolute -top-2.5 left-2 px-1 bg-white text-neutral-500 text-[10px] font-normal">
              Check-in
            </div>
            <div className="flex items-center space-x-2 px-3">
              <CalendarIcon className="w-4 h-4 text-neutral-700" />
              <span className="text-neutral-800 font-light">
                {effectiveCheckIn
                  ? DateTime.fromISO(effectiveCheckIn)
                      .setZone(selectedLocation?.date.time_zone)
                      .toFormat("EEE, MMM d")
                  : "-"}
              </span>
            </div>
          </div>

          <div className="rounded-lg ring-1 ring-neutral-200 relative flex flex-col justify-center h-full">
            <div className="absolute -top-2.5 left-2 px-1 bg-white text-neutral-500 text-[10px] font-normal">
              Check-out
            </div>
            <div className="flex items-center space-x-2 px-3">
              <CalendarIcon className="w-4 h-4 text-neutral-700" />
              <span className="text-neutral-800 font-light">
                {selectedLocation
                  ? DateTime.fromISO(selectedLocation.date.end_date)
                      .setZone(selectedLocation?.date.time_zone)
                      .toFormat("EEE, MMM d")
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg min-w-[120px] ring-1 ring-neutral-200 relative flex flex-col justify-center h-full">
          <div className="absolute -top-2.5 left-2 px-1 bg-white text-neutral-500 text-[10px] font-normal">
            Guests
          </div>
          <div className="flex items-center space-x-2 px-3">
            <UsersIcon className="w-4 h-4 text-neutral-700" />
            <span className="text-neutral-800 font-light">
              {selectedLocation?.participants?.length || 1}{" "}
              {selectedLocation?.participants?.length === 1
                ? "adult"
                : "adults"}
            </span>
          </div>
        </div>

        <CustomDropdown
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          trigger={
            <button
              className="ring-1 ring-neutral-200 hover:bg-neutral-100 rounded-lg px-3 flex items-center justify-center transition-colors relative h-full"
              aria-label="Filter options"
            >
              <FunnelIcon className="w-4 h-4 text-neutral-700" />
              {activeFilters > 0 && (
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-indigo-700 rounded-full flex items-center justify-center">
                  <span className="text-[8px] text-white font-medium">
                    {activeFilters}
                  </span>
                </div>
              )}
            </button>
          }
        >
          <div className="w-[280px]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="p-3 space-y-4"
            >
              {activeAccommodationTab === "hotels" && (
                <div>
                  <div className="text-sm font-medium text-neutral-700 mb-2">
                    Rating
                  </div>
                  <div className="flex gap-2">
                    {scores.map((score) => (
                      <button
                        key={score}
                        onClick={() => handleRatingChange(score)}
                        className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-2 font-light transition-colors
                            ${
                              selectedRating === score
                                ? "bg-indigo-100 text-indigo-700 font-medium"
                                : "bg-neutral-50 hover:bg-neutral-100 text-neutral-600"
                            }`}
                      >
                        {score}+
                        <StarIcon className="w-3.5 h-3.5 text-neutral-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-sm font-medium text-neutral-700 mb-2">
                  Price Range
                </div>
                <div className="flex flex-col gap-1">
                  {priceCategories.map((category, index) => (
                    <button
                      key={category.value}
                      onClick={() => handlePriceChange(category.value)}
                      className={`px-3 py-1.5 text-sm rounded-md text-left font-light transition-colors flex gap-1 items-center justify-between
                            ${
                              selectedPriceRange === category.value
                                ? "bg-indigo-100 text-indigo-700 font-medium"
                                : "bg-neutral-50 hover:bg-neutral-100 text-neutral-600"
                            }`}
                    >
                      {category.label}
                      <div className="flex items-center">
                        {[...Array(index + 1)].map((_, i) => (
                          <span
                            key={i}
                            className="text-xs font-light text-neutral-400"
                          >
                            $
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </CustomDropdown>
      </div>
    </div>
  );
};

export default SearchInput;
